// Sidebar — fixed left navigation rail. Replaces the old horizontal
// CustomerNav + OperatorNav strips. Groups items semantically and uses
// lucide icons for fast scanning.
//
// Mobile: collapses behind a drawer (hidden on small screens by default;
// Topbar exposes a hamburger that toggles `mobileOpen`).
//
// Active-state is derived from useLocation — matches by exact path or
// (for drill-in routes like /operator/customers/:id) by prefix.

import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Building2,
  CreditCard,
  FileText,
  Gauge,
  HardDriveDownload,
  Heart,
  LayoutDashboard,
  type LucideIcon,
  LogOut,
  PieChart,
  Plug2,
  Settings,
  Sparkles,
  Star,
  TrendingUp,
  Users,
} from 'lucide-react';
import { type SessionInfo } from '../lib/api.js';
import { Pill } from './ui.js';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Whether to match exactly (default false → prefix). */
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const CUSTOMER_GROUPS: NavGroup[] = [
  {
    label: 'Home',
    items: [{ to: '/', label: 'Overview', icon: LayoutDashboard, exact: true }],
  },
  {
    label: 'Engine',
    items: [
      { to: '/content', label: 'Content', icon: FileText },
      { to: '/gbp', label: 'Business Profile', icon: Building2 },
      { to: '/reviews', label: 'Reviews', icon: Star },
      { to: '/aieo', label: 'AI Search', icon: Sparkles },
    ],
  },
  {
    label: 'Measurement',
    items: [
      { to: '/audits', label: 'Audits', icon: Gauge },
      { to: '/site-health', label: 'Site Health', icon: Heart },
      { to: '/rank-tracker', label: 'Rank Tracker', icon: TrendingUp },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/backups', label: 'Backups', icon: HardDriveDownload },
      { to: '/integrations', label: 'Integrations', icon: Plug2 },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/settings', label: 'Settings', icon: Settings },
      { to: '/billing', label: 'Billing', icon: CreditCard },
    ],
  },
];

const OPERATOR_GROUPS: NavGroup[] = [
  {
    label: 'Operator',
    items: [
      { to: '/operator/customers', label: 'Customers', icon: Users },
      { to: '/operator/portfolio', label: 'Portfolio', icon: PieChart },
      { to: '/operator/feed', label: 'Feed', icon: Activity },
      { to: '/operator/alerts', label: 'Alerts', icon: AlertTriangle },
      { to: '/operator/autoreason', label: 'Autoreason', icon: Gauge },
    ],
  },
];

interface SidebarProps {
  session: SessionInfo;
  /** Whether the sidebar is open in mobile drawer mode. Desktop ignores this. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ session, mobileOpen, onMobileClose }: SidebarProps) {
  const isOperator = session.user.role === 'operator';
  const groups = isOperator ? OPERATOR_GROUPS : CUSTOMER_GROUPS;

  const initials = (session.user.display_name ?? session.user.email)
    .split(/[\s@]+/)
    .filter(Boolean)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('') || '·';

  const subtitle = isOperator
    ? 'Operator Console'
    : session.accounts.length === 1
      ? session.accounts[0]!.account_name
      : session.accounts.length > 1
        ? `${session.accounts.length} locations`
        : 'No account';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`md:hidden fixed inset-0 bg-black/60 z-30 transition-opacity ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onMobileClose}
      />

      <aside
        className={`fixed md:sticky top-0 left-0 z-40 h-screen w-[244px] shrink-0 border-r border-cream/[0.08] bg-zinc-950/95 backdrop-blur-md flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Brand + workspace */}
        <div className="px-5 pt-6 pb-5 border-b border-cream/[0.06]">
          <Link to="/" className="block group" onClick={onMobileClose}>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-3xl tracking-tightest text-cream group-hover:text-cream-100 transition-colors leading-none">
                Bloom
              </span>
              {isOperator && <Pill tone="amber">ops</Pill>}
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.16em] text-cream/70 font-semibold truncate">
              {subtitle}
            </div>
          </Link>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.20em] text-cream/55 font-bold">
                {group.label}
              </div>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink item={item} onClick={onMobileClose} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User pill + logout */}
        <div className="px-3 py-4 border-t border-cream/[0.06]">
          <div className="px-3 py-2.5 rounded-xl bg-cream/[0.06] backdrop-blur-md border border-cream/[0.14] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-cream/[0.14] border border-cream/[0.20] flex items-center justify-center text-[12px] font-bold text-cream shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-cream truncate">
                {session.user.display_name ?? session.user.email.split('@')[0]}
              </div>
              <div className="text-[11px] text-cream/70 truncate">
                {session.user.email}
              </div>
            </div>
          </div>
          <form action="/api/dashboard/auth/logout" method="POST" className="mt-2">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold py-2.5 rounded-full border border-cream/[0.14] bg-cream/[0.04] backdrop-blur-md text-cream/85 hover:text-cream hover:border-cream/30 hover:bg-cream/[0.10] transition-all cursor-pointer shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]"
              title="Sign out"
            >
              <LogOut size={13} strokeWidth={2.25} />
              Sign out
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const location = useLocation();
  const Icon = item.icon;
  const isActive = item.exact
    ? location.pathname === item.to
    : location.pathname === item.to || location.pathname.startsWith(item.to + '/');

  return (
    <NavLink
      to={item.to}
      end={item.exact}
      onClick={onClick}
      className={() =>
        `relative group flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-semibold transition-colors ${
          isActive
            ? 'bg-cream/[0.10] text-cream backdrop-blur-md border border-cream/[0.14] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
            : 'text-cream/80 hover:bg-cream/[0.05] hover:text-cream'
        }`
      }
    >
      {isActive && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-cream" />
      )}
      <Icon
        size={16}
        strokeWidth={isActive ? 2 : 1.75}
        className={isActive ? 'text-cream' : 'text-cream/65 group-hover:text-cream/90'}
      />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}
